/**
 * Reonic Import Page
 * - Listet Reonic-Angebote über die Reonic-REST-API
 * - Klick auf Import: holt Preview → öffnet Combined-Review-Modal mit PV/WP-Tabs
 * - 1-Klick-Import erstellt 1 PV-Angebot + 1 WP-Angebot im CRM
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { reonicAPI, customersAPI, WP_PRODUCT_GROUPS as WP_GROUPS_API } from "../lib/api";
import { toast } from "sonner";
import {
  Cable, RefreshCw, Download, ExternalLink, Loader2, AlertTriangle,
  CheckCircle, ArrowRight, FileText, Sun, Flame, ChevronLeft, ChevronRight,
  Settings as SettingsIcon, Eye, EyeOff, Save, TestTube,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";

const WP_GROUPS = WP_GROUPS_API || [
  { key: "waermepumpe", label: "Wärmepumpe" },
  { key: "speicher", label: "Speicher" },
  { key: "hydraulik", label: "Hydraulik" },
  { key: "installation", label: "Installation" },
  { key: "zusatz", label: "Zusatz" },
  { key: "sonstiges", label: "Sonstiges" },
];

const fmtEur = (v) =>
  v === null || v === undefined
    ? "—"
    : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);

function getSignatureBadge(offer) {
  const sig = offer.currentSignature || {};
  if (sig.state === "Signed") {
    return <Badge className="bg-green-600">Unterzeichnet</Badge>;
  }
  if (sig.state) {
    return <Badge variant="secondary">{sig.state}</Badge>;
  }
  return <Badge variant="outline">Kein Status</Badge>;
}

export default function ReonicImportPage() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [offers, setOffers] = useState([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [filter, setFilter] = useState("all"); // all, signed, unsigned, imported
  const [search, setSearch] = useState("");

  // Modal state
  const [importingOfferId, setImportingOfferId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerMode, setCustomerMode] = useState("create");
  const [linkCustomerId, setLinkCustomerId] = useState("");
  const [createPv, setCreatePv] = useState(true);
  const [createWp, setCreateWp] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ base_url: "https://api.reonic.de", client_id: "", api_key: "", api_key_v2: "", enabled: true });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsSource, setSettingsSource] = useState("env");
  const [apiKeyMasked, setApiKeyMasked] = useState("");
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyV2Masked, setApiKeyV2Masked] = useState("");
  const [hasApiKeyV2, setHasApiKeyV2] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiKeyV2, setShowApiKeyV2] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingSettings, setTestingSettings] = useState(false);
  const [testingV2, setTestingV2] = useState(false);
  const [v2TestResult, setV2TestResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [outboundIp, setOutboundIp] = useState(null);

  const openSettings = async () => {
    setTestResult(null);
    setV2TestResult(null);
    setSettingsLoaded(false);
    try {
      const res = await reonicAPI.getSettings();
      setSettings({
        base_url: res.data.base_url || "https://api.reonic.de",
        client_id: res.data.client_id || "",
        api_key: "",  // never expose - user types new value to overwrite
        api_key_v2: "",
        enabled: res.data.enabled,
      });
      setSettingsSource(res.data.source);
      setApiKeyMasked(res.data.api_key_masked || "");
      setHasApiKey(res.data.has_api_key);
      setApiKeyV2Masked(res.data.api_key_v2_masked || "");
      setHasApiKeyV2(!!res.data.has_api_key_v2);
      setSettingsLoaded(true);
      // Erst NACH dem Setzen des Form-States das Dialog öffnen — verhindert
      // einen Render mit "stale state" (Race Condition, iter70 LOW issue).
      setSettingsOpen(true);
      // Outbound-IP im Hintergrund holen — für Reonic-Whitelisting-Diagnose
      reonicAPI.healthExtended()
        .then((r) => setOutboundIp(r.data?.outbound_ip || null))
        .catch(() => setOutboundIp(null));
    } catch (err) {
      toast.error("Settings konnten nicht geladen werden");
    }
  };

  const handleTestSettings = async () => {
    if (!settings.base_url || !(settings.api_key || hasApiKey)) {
      toast.error("Base-URL und API-Key sind erforderlich für den Test");
      return;
    }
    setTestingSettings(true);
    setTestResult(null);
    try {
      if (!settings.api_key) {
        toast.error("Bitte den v3-API-Key eingeben (zum Testen wird er nicht gespeichert)");
        setTestingSettings(false);
        return;
      }
      const res = await reonicAPI.testSettings({
        base_url: settings.base_url,
        client_id: settings.client_id,
        api_key: settings.api_key,
        api_key_v2: settings.api_key_v2 || undefined,
      });
      setTestResult(res.data);
      if (res.data.success) toast.success(res.data.message || "v3-Verbindung OK");
      else toast.error(res.data.error || "Test fehlgeschlagen");
      if (settings.api_key_v2) {
        if (res.data.v2_success) toast.success("v2-Detail-Key OK — Einzelkomponenten verfügbar.");
        else toast.error(res.data.v2_error || "v2-Test fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Test fehlgeschlagen");
    } finally {
      setTestingSettings(false);
    }
  };

  const handleTestV2Only = async () => {
    if (!settings.base_url || !settings.client_id) {
      toast.error("Base-URL und Client-ID werden für den v2-Test benötigt");
      return;
    }
    if (!settings.api_key_v2) {
      toast.error("Bitte zuerst den v2-Detail-Key in das Feld eingeben");
      return;
    }
    setTestingV2(true);
    setV2TestResult(null);
    try {
      // Reiner v2-Test: Backend überspringt v3 (v2_only=true), wir bekommen direkt v2_*-Felder.
      const res = await reonicAPI.testSettings({
        base_url: settings.base_url,
        client_id: settings.client_id,
        api_key_v2: settings.api_key_v2,
        v2_only: true,
      });
      const v2 = {
        success: !!res.data.v2_success,
        status_code: res.data.v2_status_code,
        offer_count: res.data.v2_offer_count,
        error: res.data.v2_error,
        auth_scheme: res.data.v2_auth_scheme,
        attempts: res.data.v2_attempts || [],
      };
      setV2TestResult(v2);
      if (v2.success) {
        toast.success(`v2-Detail-Key OK — ${v2.offer_count ?? "?"} Offers über v2 erreichbar.`);
      } else {
        const code = v2.status_code ? `HTTP ${v2.status_code} — ` : "";
        toast.error(`${code}${v2.error || "v2-Test fehlgeschlagen"}`);
      }
    } catch (err) {
      // Backend-Validation oder Netzwerk-Error → vollen Fehler darstellen
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unbekannter Fehler";
      const status = err?.response?.status;
      setV2TestResult({
        success: false,
        status_code: status,
        error: typeof detail === "string" ? detail : JSON.stringify(detail),
      });
      toast.error(`v2-Test fehlgeschlagen${status ? ` (HTTP ${status})` : ""}: ${typeof detail === "string" ? detail : "siehe Konsole"}`);
    } finally {
      setTestingV2(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings.base_url || !settings.client_id) {
      toast.error("Base-URL und Client-ID sind erforderlich");
      return;
    }
    setSavingSettings(true);
    try {
      await reonicAPI.updateSettings({
        base_url: settings.base_url,
        client_id: settings.client_id,
        api_key: settings.api_key,  // leer = bestehenden behalten
        api_key_v2: settings.api_key_v2,  // leer = bestehenden behalten
        clear_api_key_v2: settings.clear_api_key_v2 || undefined,  // explizites Entfernen
        enabled: settings.enabled,
      });
      toast.success("Reonic-Credentials gespeichert");
      setSettingsOpen(false);
      // Health & Liste neu laden
      await loadHealth();
      setSettings({ ...settings, api_key: "", api_key_v2: "", clear_api_key_v2: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => { loadHealth(); loadCustomers(); }, []);
  useEffect(() => { if (health?.reachable) loadOffers(0); }, [health?.reachable]);

  const loadHealth = async () => {
    try {
      const res = await reonicAPI.health();
      setHealth(res.data);
    } catch (err) {
      setHealth({ configured: false, reachable: false, error: err?.response?.data?.detail || err.message });
    }
  };

  const loadOffers = async (p = 0) => {
    setLoadingList(true);
    try {
      const res = await reonicAPI.listOffers(p);
      setOffers(res.data.results || []);
      setHasNext(res.data.hasNextPage || false);
      setPage(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reonic-Liste konnte nicht geladen werden");
    } finally {
      setLoadingList(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await customersAPI.list();
      setCustomers(res.data || []);
    } catch {/* ignore */}
  };

  const filtered = useMemo(() => {
    let list = offers || [];
    if (filter === "imported") list = list.filter((o) => o._crm_already_imported);
    if (filter === "not_imported") list = list.filter((o) => !o._crm_already_imported);
    if (filter === "installation") list = list.filter((o) => o.stage === "installation");
    if (filter === "offer") list = list.filter((o) => o.stage === "offer");
    if (filter === "request") list = list.filter((o) => o.stage === "request");
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          (o.id || "").toLowerCase().includes(q) ||
          (o.offer_number || "").toLowerCase().includes(q) ||
          (o.name || "").toLowerCase().includes(q) ||
          (o.address_city || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [offers, filter, search]);

  const openImportModal = async (reonicOfferId) => {
    setImportingOfferId(reonicOfferId);
    setPreview(null);
    setLoadingPreview(true);
    setLinkCustomerId("");
    setCustomerMode("create");
    setCreatePv(true);
    setCreateWp(true);
    try {
      const res = await reonicAPI.preview(reonicOfferId);
      setPreview(res.data);
      // Auto-Vorschlag: WP existiert nur, wenn Positionen vorhanden
      if (!(res.data.wp_offer?.positions?.length)) setCreateWp(false);
      if (!(res.data.pv_offer?.positions?.length)) setCreatePv(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview konnte nicht geladen werden");
      setImportingOfferId(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    if (customerMode === "link" && !linkCustomerId) {
      toast.error("Bitte einen Kunden auswählen");
      return;
    }
    if (preview.already_imported) {
      toast.error("Dieses Reonic-Angebot wurde bereits importiert.");
      return;
    }
    if (!createPv && !createWp) {
      toast.error("Mindestens PV oder WP muss übernommen werden");
      return;
    }

    setSubmitting(true);
    try {
      const customer = preview.customer || {};
      const newCustomer = customerMode === "create" ? {
        salutation: customer.salutation || "Herr",
        first_name: (customer.name || "").split(/\s+/)[0] || "",
        last_name: (customer.name || "").split(/\s+/).slice(1).join(" ") || "",
        address: [customer.street, [customer.zip, customer.city].filter(Boolean).join(" ")].filter(Boolean).join("\n"),
        email: customer.email,
        phone: customer.phone,
      } : null;

      const payload = {
        customer_id: customerMode === "link" ? linkCustomerId : null,
        create_customer: newCustomer,
        create_pv: createPv,
        create_wp: createWp,
        pv_offer: createPv ? {
          title: `PV-Angebot Reonic ${preview.shared?.pv_system_kwp ? preview.shared.pv_system_kwp + ' kWp' : ''}`.trim(),
          offer_date: preview.shared?.offer_date || new Date().toISOString().slice(0, 10),
          vat_rate: 0,
          positions: preview.pv_offer?.positions || [],
          discount: preview.pv_offer?.discount,
        } : null,
        wp_offer: createWp ? {
          title: `Wärmepumpen-Angebot Reonic ${preview.shared?.hp_model || ''}`.trim(),
          vat_rate: 19,
          positions: preview.wp_offer?.positions || [],
          heat_demand_kwh: preview.wp_offer?.hp_specifics?.heat_demand_kwh,
          current_heating_type: preview.wp_offer?.hp_specifics?.current_heating_type,
        } : null,
      };

      const res = await reonicAPI.import(importingOfferId, payload);
      toast.success(res.data.message || "Reonic-Import erfolgreich");
      setImportingOfferId(null);
      // Refresh, damit der "imported"-Badge erscheint
      loadOffers(page);
      // Navigieren zum erstellten Angebot
      if (res.data.pv_offer_id) {
        navigate(`/angebote-import/${res.data.pv_offer_id}`);
      } else if (res.data.wp_offer_id) {
        navigate(`/wp-angebote/${res.data.wp_offer_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render ----

  if (!health) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="reonic-loading-health" />
      </div>
    );
  }

  if (!health.configured) {
    return (
      <div className="space-y-4" data-testid="reonic-not-configured">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Cable className="h-6 w-6 text-blue-500" /> Reonic Import
        </h1>
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            <strong>Reonic-Integration ist nicht konfiguriert.</strong><br />
            Bitte hinterlegen Sie die Credentials über die Schaltfläche unten.
          </AlertDescription>
        </Alert>
        <Button onClick={openSettings} data-testid="open-settings-not-configured">
          <SettingsIcon className="h-4 w-4 mr-2" /> Reonic-Credentials hinterlegen
        </Button>
        <ReonicSettingsDialog
          open={settingsOpen} onOpenChange={setSettingsOpen}
          settings={settings} setSettings={setSettings}
          hasApiKey={hasApiKey} apiKeyMasked={apiKeyMasked}
          hasApiKeyV2={hasApiKeyV2} apiKeyV2Masked={apiKeyV2Masked}
          settingsSource={settingsSource}
          showApiKey={showApiKey} setShowApiKey={setShowApiKey}
          showApiKeyV2={showApiKeyV2} setShowApiKeyV2={setShowApiKeyV2}
          onTest={handleTestSettings} onSave={handleSaveSettings}
          testing={testingSettings} saving={savingSettings} testResult={testResult}
          onTestV2={handleTestV2Only} testingV2={testingV2}
          v2TestResult={v2TestResult} setV2TestResult={setV2TestResult}
          outboundIp={outboundIp}
        />
      </div>
    );
  }

  if (!health.reachable) {
    return (
      <div className="space-y-4" data-testid="reonic-unreachable">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Cable className="h-6 w-6 text-red-500" /> Reonic Import
        </h1>
        <Alert className="border-red-300 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription>
            <strong>Reonic-API ist nicht erreichbar.</strong><br />
            {health.error || "Bitte API-Key und Client-ID prüfen."}
          </AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button onClick={loadHealth} variant="outline" data-testid="retry-health">
            <RefreshCw className="h-4 w-4 mr-2" /> Erneut prüfen
          </Button>
          <Button onClick={openSettings} data-testid="open-settings-unreachable">
            <SettingsIcon className="h-4 w-4 mr-2" /> Credentials bearbeiten
          </Button>
        </div>
        <ReonicSettingsDialog
          open={settingsOpen} onOpenChange={setSettingsOpen}
          settings={settings} setSettings={setSettings}
          hasApiKey={hasApiKey} apiKeyMasked={apiKeyMasked}
          hasApiKeyV2={hasApiKeyV2} apiKeyV2Masked={apiKeyV2Masked}
          settingsSource={settingsSource}
          showApiKey={showApiKey} setShowApiKey={setShowApiKey}
          showApiKeyV2={showApiKeyV2} setShowApiKeyV2={setShowApiKeyV2}
          onTest={handleTestSettings} onSave={handleSaveSettings}
          testing={testingSettings} saving={savingSettings} testResult={testResult}
          onTestV2={handleTestV2Only} testingV2={testingV2}
          v2TestResult={v2TestResult} setV2TestResult={setV2TestResult}
          outboundIp={outboundIp}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reonic-import-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Cable className="h-7 w-7 text-blue-500" /> Reonic Import
          </h1>
          <p className="text-sm text-muted-foreground">
            Direkter Import aus Reonic h360 — Client: <code>{health.client_id}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openSettings} data-testid="open-settings-main">
            <SettingsIcon className="h-4 w-4 mr-2" /> Einstellungen
          </Button>
          <Button variant="outline" size="sm" onClick={() => loadOffers(page)} data-testid="refresh-offers">
            <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Suche nach Offer-ID, Quelle..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="reonic-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px]" data-testid="reonic-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Projekte</SelectItem>
              <SelectItem value="request">Anfragen (request)</SelectItem>
              <SelectItem value="offer">Angebote (offer)</SelectItem>
              <SelectItem value="installation">In Installation</SelectItem>
              <SelectItem value="imported">Bereits importiert</SelectItem>
              <SelectItem value="not_imported">Noch nicht importiert</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loadingList ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Keine Reonic-Angebote gefunden.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reonic-Angebotsnr.</TableHead>
                  <TableHead>Kunde / Adresse</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Gesamtpreis</TableHead>
                  <TableHead>Pakete</TableHead>
                  <TableHead>CRM-Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => {
                  const sig = o.currentSignature || {};
                  const imported = o._crm_already_imported;
                  const packages = [];
                  if (o.solarPackage?.name) packages.push(<Badge key="pv" variant="outline" className="text-xs"><Sun className="h-3 w-3 mr-1" />PV</Badge>);
                  if (o.sesPackage?.name) packages.push(<Badge key="bat" variant="outline" className="text-xs">Speicher</Badge>);
                  if (o.heatpumpPackage?.name) packages.push(<Badge key="wp" variant="outline" className="text-xs"><Flame className="h-3 w-3 mr-1" />WP</Badge>);
                  if (o.wallboxPackage?.name) packages.push(<Badge key="wb" variant="outline" className="text-xs">Wallbox</Badge>);
                  return (
                    <TableRow key={o.id} data-testid={`reonic-row-${o.id}`}>
                      <TableCell className="font-mono text-xs">
                        {o.offer_number ? (
                          <strong>#{o.offer_number}</strong>
                        ) : o.display_label ? (
                          <strong className="text-amber-700" title="Reonic hat keine Angebotsnummer geliefert — Fallback-Bezeichner">
                            Projekt: {o.display_label}
                          </strong>
                        ) : (
                          <span className="text-muted-foreground">{(o.id || "").slice(0, 8)}…</span>
                        )}
                        {(o.offer_number || o.display_label) && (
                          <div className="text-muted-foreground">{(o.id || "").slice(0, 8)}…</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{o.name || "—"}</div>
                        {(o.address_zip || o.address_city) && (
                          <div className="text-xs text-muted-foreground">
                            {[o.address_zip, o.address_city].filter(Boolean).join(" ")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {o.offerCreatedAt ? new Date(o.offerCreatedAt).toLocaleDateString("de-DE") : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={o.stage === "installation" ? "default" : "outline"} className={o.stage === "installation" ? "bg-green-600" : ""}>
                          {o.stage || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{fmtEur(o.totalPlannedPrice)}</TableCell>
                      <TableCell><div className="flex gap-1 flex-wrap">{packages}</div></TableCell>
                      <TableCell>
                        {imported ? (
                          <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Importiert</Badge>
                        ) : (
                          <Badge variant="outline">Offen</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {sig.signedPdfUrl && (
                            <Button
                              size="sm"
                              variant="ghost"
                              asChild
                              data-testid={`view-signed-pdf-${o.id}`}
                              title="Signiertes PDF anzeigen"
                            >
                              <a href={sig.signedPdfUrl} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {!imported && (
                            <Button
                              size="sm"
                              onClick={() => openImportModal(o.id)}
                              data-testid={`import-btn-${o.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" /> Import
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button
          size="sm" variant="outline" onClick={() => loadOffers(Math.max(0, page - 1))}
          disabled={page === 0 || loadingList}
          data-testid="prev-page"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Vorherige
        </Button>
        <div className="text-sm text-muted-foreground">Seite {page + 1}</div>
        <Button
          size="sm" variant="outline" onClick={() => loadOffers(page + 1)}
          disabled={!hasNext || loadingList}
          data-testid="next-page"
        >
          Nächste <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* IMPORT MODAL */}
      <Dialog open={!!importingOfferId} onOpenChange={(open) => !open && setImportingOfferId(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cable className="h-5 w-5" /> Reonic-Angebot importieren
            </DialogTitle>
            <DialogDescription>
              Es werden 1 PV-Angebot (0% MwSt.) und/oder 1 WP-Angebot (19% MwSt.) im CRM erstellt.
            </DialogDescription>
          </DialogHeader>

          {loadingPreview && (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}

          {!loadingPreview && preview && (
            <div className="space-y-4">
              {/* Import-Strategie Banner: zeigt welcher Pfad genutzt wurde (v2 detail / v3 fallback) */}
              {preview.import_diagnostics && (() => {
                const d = preview.import_diagnostics;
                const strategy = d.strategy;
                const isV2 = strategy === "v2_components";
                const isPauschal = strategy === "v3_pauschal_fallback";
                const bg = isV2
                  ? "border-green-300 bg-green-50"
                  : isPauschal
                    ? "border-red-300 bg-red-50"
                    : "border-amber-300 bg-amber-50";
                const icon = isV2
                  ? <CheckCircle className="h-4 w-4 text-green-600" />
                  : <AlertTriangle className={`h-4 w-4 ${isPauschal ? "text-red-600" : "text-amber-600"}`} />;
                const label = isV2
                  ? `Import-Strategie: v2-Detail (${d.components_extracted} Einzelpositionen)`
                  : strategy === "v3_components_inline"
                    ? `Import-Strategie: v3 mit Inline-Komponenten (${d.components_extracted} Positionen)`
                    : strategy === "v3_signals_placeholder"
                      ? "Import-Strategie: v3-Platzhalter (0,00 €) pro angefragtem Paket"
                      : "Import-Strategie: v3-Pauschal-Fallback (1 Position mit Gesamtsumme)";
                return (
                  <Alert className={bg} data-testid="reonic-import-strategy-banner">
                    {icon}
                    <AlertDescription>
                      <div className="font-semibold">{label}</div>
                      <div className="text-xs mt-1 text-muted-foreground space-y-0.5">
                        <div>
                          v2-Key konfiguriert: <strong>{d.v2_configured ? "Ja" : "Nein"}</strong>
                          {d.v2_attempted && (
                            <> · v2-Aufruf:&nbsp;
                              {d.v2_success
                                ? <span className="text-green-700 font-medium">HTTP 200 (OK)</span>
                                : <span className="text-red-700 font-medium">HTTP {d.v2_status_code ?? "?"} {d.v2_status_code === 401 ? "Unauthorized" : d.v2_status_code === 404 ? "Not Found" : ""}</span>}
                            </>
                          )}
                        </div>
                        {d.v2_tried_ids?.length > 0 && (
                          <div>v2 versuchte IDs: <code>{d.v2_tried_ids.join(" → ")}</code></div>
                        )}
                        {d.v2_skipped_reason && <div className="text-red-700">{d.v2_skipped_reason}</div>}
                        {d.v2_error && !d.v2_success && (
                          <div className="text-red-700 break-all">v2-Fehler: {d.v2_error.slice(0, 200)}</div>
                        )}
                      </div>
                      {!d.v2_configured && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => { setImportingOfferId(null); openSettings(); }}
                          data-testid="open-settings-from-banner"
                        >
                          <SettingsIcon className="h-4 w-4 mr-2" /> v2-Detail-Key jetzt hinterlegen
                        </Button>
                      )}
                    </AlertDescription>
                  </Alert>
                );
              })()}

              {(preview.warnings || []).length > 0 && (
                <Alert className="border-amber-300 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </AlertDescription>
                </Alert>
              )}

              <Card>
                <CardHeader><CardTitle className="text-sm">Reonic-Stammdaten</CardTitle></CardHeader>
                <CardContent className="grid md:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">PV-Anlage:</span> <strong>{preview.shared?.pv_system_kwp ?? "—"} kWp</strong></div>
                  <div><span className="text-muted-foreground">Speicher:</span> <strong>{preview.shared?.storage_kwh ?? "—"} kWh</strong></div>
                  <div><span className="text-muted-foreground">Wärmepumpe:</span> <strong>{preview.shared?.hp_model || "—"}</strong></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-sm">Kunde</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant={customerMode === "create" ? "default" : "outline"} onClick={() => setCustomerMode("create")} data-testid="modal-mode-create">
                      Neu anlegen
                    </Button>
                    <Button size="sm" variant={customerMode === "link" ? "default" : "outline"} onClick={() => setCustomerMode("link")} data-testid="modal-mode-link">
                      Bestehenden verknüpfen
                    </Button>
                  </div>
                  {customerMode === "create" ? (
                    <div className="text-sm p-3 bg-muted rounded">
                      <strong>{preview.customer?.name || "—"}</strong><br />
                      {preview.customer?.street}<br />
                      {preview.customer?.zip} {preview.customer?.city}<br />
                      {preview.customer?.email && <span className="text-muted-foreground">{preview.customer.email}</span>}<br />
                      {preview.customer?.phone && <span className="text-muted-foreground">{preview.customer.phone}</span>}
                    </div>
                  ) : (
                    <Select value={linkCustomerId} onValueChange={setLinkCustomerId}>
                      <SelectTrigger data-testid="modal-customer-select"><SelectValue placeholder="Kunde auswählen" /></SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={createPv} onCheckedChange={setCreatePv} data-testid="modal-create-pv" disabled={!preview.pv_offer?.positions?.length} />
                    <Sun className="h-4 w-4 text-yellow-500" />
                    PV-Angebot ({preview.pv_offer?.positions?.length || 0} Pos., {fmtEur(preview.pv_offer?.total_gross)})
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={createWp} onCheckedChange={setCreateWp} data-testid="modal-create-wp" disabled={!preview.wp_offer?.positions?.length} />
                    <Flame className="h-4 w-4 text-orange-500" />
                    WP-Angebot ({preview.wp_offer?.positions?.length || 0} Pos., {fmtEur(preview.wp_offer?.total_gross)})
                  </label>
                </CardContent>
              </Card>

              <Tabs defaultValue="pv">
                <TabsList>
                  <TabsTrigger value="pv"><Sun className="h-3 w-3 mr-1" /> PV ({preview.pv_offer?.positions?.length || 0})</TabsTrigger>
                  <TabsTrigger value="wp"><Flame className="h-3 w-3 mr-1" /> WP ({preview.wp_offer?.positions?.length || 0})</TabsTrigger>
                </TabsList>
                <TabsContent value="pv">
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bezeichnung</TableHead>
                            <TableHead className="text-right">Menge</TableHead>
                            <TableHead className="text-right">EP</TableHead>
                            <TableHead className="text-right">Gesamt</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(preview.pv_offer?.positions || []).map((p, i) => (
                            <TableRow key={i}>
                              <TableCell>{p.title}{p.description && <span className="text-xs text-muted-foreground block">{p.description}</span>}</TableCell>
                              <TableCell className="text-right">{p.quantity} {p.unit}</TableCell>
                              <TableCell className="text-right">{fmtEur(p.unit_price_net)}</TableCell>
                              <TableCell className="text-right font-semibold">{fmtEur(p.total_price_net)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="wp">
                  {WP_GROUPS.map((g) => {
                    const items = (preview.wp_offer?.positions || []).filter((p) => (p.product_group || "sonstiges") === g.key);
                    if (items.length === 0) return null;
                    return (
                      <Card key={g.key} className="mb-3">
                        <CardHeader className="pb-2"><CardTitle className="text-sm">{g.label}</CardTitle></CardHeader>
                        <CardContent className="p-0">
                          <Table>
                            <TableBody>
                              {items.map((p, i) => (
                                <TableRow key={i}>
                                  <TableCell>{p.title}</TableCell>
                                  <TableCell className="text-right">{p.quantity} {p.unit}</TableCell>
                                  <TableCell className="text-right">{fmtEur(p.unit_price)}</TableCell>
                                  <TableCell className="text-right font-semibold">{fmtEur(p.quantity * p.unit_price)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    );
                  })}
                </TabsContent>
              </Tabs>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportingOfferId(null)} disabled={submitting} data-testid="modal-cancel">
              Abbrechen
            </Button>
            <Button onClick={handleImport} disabled={submitting || loadingPreview || !!preview?.already_imported} data-testid="modal-confirm-import">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Jetzt importieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReonicSettingsDialog
        open={settingsOpen} onOpenChange={setSettingsOpen}
        settings={settings} setSettings={setSettings}
        hasApiKey={hasApiKey} apiKeyMasked={apiKeyMasked}
        hasApiKeyV2={hasApiKeyV2} apiKeyV2Masked={apiKeyV2Masked}
        settingsSource={settingsSource}
        showApiKey={showApiKey} setShowApiKey={setShowApiKey}
        showApiKeyV2={showApiKeyV2} setShowApiKeyV2={setShowApiKeyV2}
        onTest={handleTestSettings} onSave={handleSaveSettings}
        testing={testingSettings} saving={savingSettings} testResult={testResult}
        onTestV2={handleTestV2Only} testingV2={testingV2}
        v2TestResult={v2TestResult} setV2TestResult={setV2TestResult}
        outboundIp={outboundIp}
      />
    </div>
  );
}

// ============== REONIC SETTINGS DIALOG ==============

function ReonicSettingsDialog({
  open, onOpenChange, settings, setSettings,
  hasApiKey, apiKeyMasked,
  hasApiKeyV2, apiKeyV2Masked,
  settingsSource,
  showApiKey, setShowApiKey,
  showApiKeyV2, setShowApiKeyV2,
  onTest, onSave, testing, saving, testResult,
  onTestV2, testingV2, v2TestResult, setV2TestResult,
  outboundIp,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" /> Reonic-Verbindung konfigurieren
          </DialogTitle>
          <DialogDescription>
            Tragen Sie hier Ihren Reonic-API-Key und die Client-ID ein. Die Daten werden
            verschlüsselt in der Datenbank gespeichert und gelten ab sofort für alle Nutzer.
            Den Key erhalten Sie in Reonic unter <strong>Settings → Integrations</strong>.
          </DialogDescription>
        </DialogHeader>

        {settingsSource === "env" && (
          <Alert className="border-blue-300 bg-blue-50">
            <AlertDescription className="text-sm">
              Aktuell werden die Werte aus den Server-Umgebungsvariablen (<code>.env</code>) verwendet.
              Sobald Sie hier speichern, übernehmen die Datenbank-Werte den Vorrang.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <Label>Base-URL *</Label>
            <Input
              value={settings.base_url}
              onChange={(e) => setSettings({ ...settings, base_url: e.target.value })}
              placeholder="https://api.reonic.de"
              data-testid="settings-base-url"
            />
          </div>
          <div>
            <Label>Client-ID (UUID) *</Label>
            <Input
              value={settings.client_id}
              onChange={(e) => setSettings({ ...settings, client_id: e.target.value })}
              placeholder="z.B. fbd4840b-654d-42e7-aadb-2ff75204e9a4"
              data-testid="settings-client-id"
            />
          </div>
          <div>
            <Label>v3 API-Key (Listen-Abruf) {hasApiKey && <span className="text-xs text-muted-foreground">(aktuell: {apiKeyMasked} — leer lassen um beizubehalten)</span>}</Label>
            <div className="relative">
              <Input
                type={showApiKey ? "text" : "password"}
                value={settings.api_key}
                onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                placeholder={hasApiKey ? "•••••••••••••" : "rnc_v3_xxxxx_xxxxx_xxxxx"}
                data-testid="settings-api-key"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Wird für die Projektliste (<code>/rest/v3/residentialProjects</code>) verwendet.
            </p>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
            <Label className="flex items-center gap-2">
              v2 Detail-Key <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-normal">optional</span>
              {hasApiKeyV2 && <span className="text-xs text-muted-foreground">(aktuell: {apiKeyV2Masked} — leer lassen um beizubehalten)</span>}
            </Label>
            <p className="text-xs text-amber-900/80">
              Mit einem zusätzlichen <strong>v2-API-Key</strong> werden beim Import die <strong>echten Einzelpositionen</strong>
              (Modelle, Mengen, Einzelpreise, Steuersätze) über <code>/rest/v2/clients/&#123;clientId&#125;/h360/offers/&#123;id&#125;</code>
              geladen — statt nur des Gesamtpreises. Ohne v2-Key fällt das System auf 0,00 €-Platzhalter
              je angefragtem Paket zurück. Erstellbar in Reonic unter <strong>Settings → Integrations</strong>
              (Typ: <code>v2 / h360</code>).
            </p>
            <div className="relative">
              <Input
                type={showApiKeyV2 ? "text" : "password"}
                value={settings.api_key_v2 || ""}
                onChange={(e) => { setSettings({ ...settings, api_key_v2: e.target.value }); setV2TestResult(null); }}
                placeholder={hasApiKeyV2 ? "•••••••••••••" : "v2-Detail-Key (HTTP Basic)"}
                data-testid="settings-api-key-v2"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowApiKeyV2(!showApiKeyV2)}
              >
                {showApiKeyV2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onTestV2}
                disabled={testingV2 || !settings.api_key_v2}
                data-testid="settings-test-v2-button"
              >
                {testingV2 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
                v2-Verbindung testen
              </Button>
              {hasApiKeyV2 && (
                <button
                  type="button"
                  onClick={() => { setSettings({ ...settings, api_key_v2: "", clear_api_key_v2: true }); setV2TestResult(null); }}
                  className="text-xs text-red-700 hover:underline"
                  data-testid="settings-clear-v2"
                >
                  v2-Key entfernen
                </button>
              )}
              {outboundIp && (
                <span className="ml-auto text-xs text-muted-foreground" title="Outbound-IP für Reonic-Whitelisting">
                  Server-IP: <code data-testid="outbound-ip-inline" className="font-mono">{outboundIp}</code>
                </span>
              )}
            </div>
            {v2TestResult && (
              <div
                className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 border ${
                  v2TestResult.success
                    ? "bg-green-50 border-green-300 text-green-900"
                    : "bg-red-50 border-red-300 text-red-900"
                }`}
                data-testid="v2-test-result"
              >
                {v2TestResult.success ? (
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-600" />
                )}
                <div className="flex-1">
                  {v2TestResult.success ? (
                    <>
                      <div className="font-semibold">
                        v2-Detail-Key gültig
                        {v2TestResult.auth_scheme && (
                          <span className="ml-2 text-xs font-normal text-green-700">
                            (Auth: {v2TestResult.auth_scheme === "basic" ? "Basic clientId:key" : "Plain X-Authorization"})
                          </span>
                        )}
                      </div>
                      <div className="text-xs">
                        Reonic v2 antwortet mit HTTP 200 — {v2TestResult.offer_count ?? "?"} Offers
                        sind über den Detail-Endpoint verfügbar. Einzelpositionen werden beim Import
                        extrahiert.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold">
                        v2-Test fehlgeschlagen
                        {v2TestResult.status_code ? ` — HTTP ${v2TestResult.status_code}` : ""}
                      </div>
                      <div className="text-xs break-words whitespace-pre-wrap mt-1" data-testid="v2-test-error-detail">
                        {v2TestResult.error
                          ? v2TestResult.error
                          : v2TestResult.status_code
                            ? `HTTP ${v2TestResult.status_code} — keine weiteren Details vom Server. Bitte Backend-Logs (/var/log/supervisor/backend.*.log) prüfen.`
                            : "Keine Antwort vom Server (Timeout, CORS, oder Service down). Bitte Backend-Logs prüfen."}
                      </div>
                      {v2TestResult.attempts && v2TestResult.attempts.length > 0 && (
                        <div className="text-xs mt-2 px-2 py-1.5 bg-red-100/70 rounded">
                          <div className="font-semibold mb-0.5">Versuchte Auth-Schemes:</div>
                          <ul className="space-y-0.5">
                            {v2TestResult.attempts.map((a, i) => (
                              <li key={i} className="font-mono">
                                · {a.scheme === "basic" ? "Basic base64(clientId:key)" : "Plain X-Authorization"}
                                {" → "}
                                {a.status_code === 200
                                  ? <span className="text-green-700">200 OK ({a.offer_count} Offers)</span>
                                  : <span className="text-red-700">HTTP {a.status_code ?? "ERR"}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {v2TestResult.status_code === 401 && (
                        <div className="text-xs mt-2 px-2 py-1.5 bg-red-100 rounded">
                          → <strong>Lösung:</strong> Beide Auth-Schemata (Basic + Plain) wurden abgelehnt.
                          Der Key ist entweder kein v2/h360-Key, abgelaufen, oder wurde widerrufen.
                          In Reonic <strong>Settings → Integrations</strong> einen <strong>neuen Key vom Typ
                          h360 (v2)</strong> erzeugen — nicht den v3-Key wiederverwenden.
                          {outboundIp && (
                            <div className="mt-1.5 pt-1.5 border-t border-red-200">
                              Falls Reonic ein <strong>IP-Whitelisting</strong> erzwingt: unsere Server-IP
                              lautet <code data-testid="outbound-ip-error" className="bg-white px-1.5 py-0.5 rounded border border-red-300 font-mono">{outboundIp}</code>
                              — bitte dem Reonic-Support mitteilen.
                            </div>
                          )}
                        </div>
                      )}
                      {v2TestResult.status_code === 403 && (
                        <div className="text-xs mt-2 px-2 py-1.5 bg-red-100 rounded">
                          → <strong>Lösung:</strong> Der v2-Key ist gültig, hat aber keine Rechte
                          für <code>/h360/offers</code>. Im Reonic-Portal die <strong>Scopes</strong> des
                          v2-Keys prüfen oder einen neuen Key mit h360-Zugriff erzeugen.
                        </div>
                      )}
                      {v2TestResult.status_code === 404 && (
                        <div className="text-xs mt-2 px-2 py-1.5 bg-red-100 rounded">
                          → <strong>Lösung:</strong> Reonic kennt diesen Endpoint nicht.
                          Bitte Base-URL prüfen — sollte <code>https://api.reonic.de</code> sein
                          (nicht z. B. <code>app.reonic.de</code>).
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {testResult && (
            <Alert className={testResult.success ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}>
              <AlertDescription className="text-sm">
                {testResult.success ? (
                  <span className="text-green-800">
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    {testResult.message}
                  </span>
                ) : (
                  <span className="text-red-800">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    {testResult.error}
                  </span>
                )}
                {testResult.v2_success !== undefined && (
                  <div className={`mt-1 ${testResult.v2_success ? "text-green-800" : "text-red-800"}`}>
                    v2: {testResult.v2_success
                      ? `OK (${testResult.v2_offer_count ?? "?"} Offers)`
                      : (testResult.v2_error || `HTTP ${testResult.v2_status_code}`)}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="settings-cancel">
            Abbrechen
          </Button>
          <Button variant="outline" onClick={onTest} disabled={testing || saving} data-testid="settings-test-button">
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
            Verbindung testen
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="settings-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
